<template>
  <div :class="[loading ? 'loading large' : '']">
    <lila-message :alert-data="alert" />

    <div class="kanban">
      <div
        v-for="status in sortedStatusTypes"
        :key="status.Id"
        class="kanban-column"
        @dragover.prevent
        @drop="onDrop(status.Id)"
      >
        <h2 class="kanban-header">
          {{ status.Name }}
        </h2>

        <template v-if="status.Id === lastStatusId">
          <div
            v-for="card in actionsByStatus[status.Id]"
            :key="card.Id"
            :style="{ color: card.color }"
            :class="['card']"
            draggable="true"
            @dragstart="onDragStart(card, status.Id)"
          >
            <div
              class="kanban-card-content is-cursor-pointer"
              @click="openClosedActionDetails(card)"
            >
              <h3>{{ card.title }}</h3>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            v-for="(card, index) in actionsByStatus[status.Id]"
            :key="card.Id"
            :style="{ color: card.color }"
            :class="['card']"
            draggable="true"
            @dragstart="onDragStart(card, status.Id)"
          >
            <div class="kanban-card-content">
              <div class="kanban-card-header">
                <h3
                  class="is-cursor-pointer"
                  @click="openActionDetails(card)"
                >
                  {{ card.title }}
                </h3>
                <lila-svg-icon
                  :src="require(`@assets/icons/delete.svg`)"
                  class="icon delete-icon is-cursor-pointer"
                  @dblclick.stop
                  @custom-click-event="
                    openDeleteConfirm(card, index, status.Id)
                  "
                />
              </div>
              <div class="kanban-card-footer">
                <div class="card-info-item card-professional">
                  <img
                    :src="
                      card.assignedProfessional?.Photo ||
                        require('@/assets/images/default_avatar_neutral.png')
                    "
                    class="circular-mask small-image-25"
                  >
                  <span class="card-info-text">
                    {{ card.assignedProfessional?.Name || 'Atribuir' }}
                  </span>
                </div>
                <div
                  v-show="card.dueDate"
                  class="card-info-item card-due-date"
                >
                  <span class="card-info-text">
                    Prazo: {{ $Utilities.formatDate(card.dueDate, "YYYY-MM-DD", "DD/MM") }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Botão para abrir o popup de ações -->
    <lila-button
      id="addAction"
      type="button"
      title="Adicionar"
      @click="showNewActionPopup = true"
    />

    <!-- Popup para adicionar ações -->
    <CreateActionPopup
      v-if="showNewActionPopup"
      :alert="alert"
      :professionals="professionals"
      @close="showNewActionPopup = false"
      @action-created="handleActionCreated"
    />

    <!-- Popup para definir desfecho -->
    <ActionOutcomePopup
      v-if="showOutcomePopup"
      :dragged-card="draggedCard"
      :mapped-outcome-options="mappedOutcomeOptions"
      :alert="alert"
      :last-status-id="lastStatusId"
      @close="handleOutcomePopupClose"
      @outcome-saved="handleOutcomeSaved"
    />

    <!-- Popup para exibir ação encerrada -->
    <DoneActionPopup
      v-if="showDoneActionPopup"
      :card="doneActionSelected"
      @close="showDoneActionPopup = false"
    />

    <DeleteActionPopup
      v-if="showDeletePopup && actionToDelete"
      :item-to-delete="actionToDelete"
      @close="closeDeleteModal"
      @confirm="handleDeleteAction"
    />

    <!-- Detalhes da ação -->
    <ActionDetailsPopup
      v-if="showActionDetailsPopup && actionDetailsSelected"
      :card="actionDetailsSelected"
      :professionals="professionals"
      @close="closeActionDetails"
      @save="handleActionSave"
    />
  </div>
</template>

<script>
import CreateActionPopup from "../../../components/Actions/CreateActionPopup.vue";
import Button from "@components/shared/button/Button.vue";
import ActionOutcomePopup from "../../../components/Actions/ActionOutcomePopup.vue";
import DoneActionPopup from "../../../components/Actions/DoneActionPopup.vue";
import DeleteActionPopup from "@components/Actions/DeleteActionPopup.vue";
import ActionDetailsPopup from "@components/Actions/ActionDetailsPopup.vue";
import Message from "@components/shared/message/Message.vue";
import SvgIcon from "@components/shared/images/SvgIcon.vue";

export default {
  components: {
    CreateActionPopup,
    ActionOutcomePopup,
    DoneActionPopup,
    DeleteActionPopup,
    ActionDetailsPopup,
    "lila-button": Button,
    "lila-message": Message,
    "lila-svg-icon": SvgIcon
  },
  data() {
    return {
      showNewActionPopup: false,
      showOutcomePopup: false,
      showDoneActionPopup: false,
      showDeletePopup: false,
      showActionDetailsPopup: false,
      actionDetailsSelected: null,
      doneActionSelected: null,
      actionToDelete: null,
      actionToDeleteIndex: null,
      actionToDeleteColumn: null,
      actionsByStatus: {},
      draggedCard: null,
      sourceColumn: null,
      professionals: [],
      alert: {
        message: "",
        type: "",
        show: false
      },  
      outcomeOptions: [],
      loading: false,
      statusTypes: []
    };
  },
  computed: {
    mappedOutcomeOptions() {
      return this.outcomeOptions.map((option) => ({
        _id: option.Id,
        name: option.Name
      }));
    },
    sortedStatusTypes() {
      return [...this.statusTypes].sort((a, b) => a.Id - b.Id);
    },
    firstStatusId() {
      return this.sortedStatusTypes.length > 0
        ? this.sortedStatusTypes[0].Id
        : null;
    },
    lastStatusId() {
      return this.sortedStatusTypes.length > 0
        ? this.sortedStatusTypes[this.sortedStatusTypes.length - 1].Id
        : null;
    }
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      await this.loadStatusTypes();

      this.loadActions();
      this.loadProfessionals();
      this.loadOutcomeTypes();
    },

    async loadStatusTypes() {
      await this.$HTTPUtilities.get(
        this,
        "/api/action/statustypes",
        (res) => {
          if (Array.isArray(res)) {
            this.statusTypes = res;
          } else if (Array.isArray(res.data)) {
            this.statusTypes = res.data;
          } else if (res.data && res.data.ActionStatustypes) {
            this.statusTypes = res.data.ActionStatustypes;
          }

          const newActionsByStatus = {};
          this.statusTypes.forEach((status) => {
            newActionsByStatus[status.Id] = [];
          });
          this.actionsByStatus = newActionsByStatus;
        },
        this.alert
      );
    },

    normalizeString(str) {
      return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },

    getStatusIdByName(statusName) {
      if (!this.statusTypes || this.statusTypes.length === 0) {
        console.error("statusTypes não carregado!");
        return 0;
      }

      const normalizedInput = this.normalizeString(statusName);
      const statusType = this.statusTypes.find(
        (st) => this.normalizeString(st.Name) === normalizedInput
      );

      if (!statusType) {
        return this.firstStatusId || 0;
      }

      return statusType.Id;
    },

    async loadActions() {
      this.loading = true;

      this.statusTypes.forEach((status) => {
        this.actionsByStatus[status.Id] = [];
      });

      await this.$HTTPUtilities.get(
        this,
        `/api/action/patient/${this.$route.params.id}`,
        (res) => {
          res.data.forEach((action) => {
            const statusId = this.getStatusIdByName(action.Status);

            const cardData = {
              Id: action.Id,
              title: action.ActionType.Name,
              color: this.chooseCategoryColor(action.ActionType),
              dueDate: action.DueDate,
              description: action.Description,
              assignedProfessional: {
                Id: action.ProfessionalAssigned?.Id,
                Name: action.ProfessionalAssigned?.Name,
                Photo: action.ProfessionalAssigned?.Photo
              },
              status: statusId,
              actionOutcomeObservations: action.ActionOutcomeObservations,
              conclusionDate: action.ConclusionDateAndTime
            };

            if (this.actionsByStatus[statusId]) {
              this.actionsByStatus[statusId].push(cardData);
            } else {
              console.warn(
                `Status "${action.Status}" (ID: ${statusId}) não encontrado nas colunas disponíveis`
              );
            }
          });

          this.loading = false;
        },
        this.alert
      );
    },
    async loadOutcomeTypes() {
      await this.$HTTPUtilities.get(
        this,
        "/api/action/outcomes",
        (res) => {
          res.data.ActionOutcomes.forEach((outcome) => {
            this.outcomeOptions.push(outcome);
          });
        },
        this.alert
      );
    },
    async loadProfessionals() {
      await this.$HTTPUtilities.get(
        this,
        "/api/professional?isActive=true",
        (res) => {
          res.data.forEach((professional) => {
            this.professionals.push({
              Id: professional.Id,
              Name: professional.Name,
              Photo: professional.Photo
            });
          });
        },
        this.alert
      );
    },
    async updateAction(card) {
      const payload = this.mountUpdateActionJSON(card);
      await this.$HTTPUtilities.put(
        this,
        `/api/action/${card.Id}/patient/${this.$route.params.id}`,
        payload,
        this.alert
      );
    },
    mountUpdateActionJSON(card) {
      return {
        ProfessionalAssignedId: card.assignedProfessional?.Id || null,
        DueDate: card.dueDate,
        Status: card.status,
        Description: card.description ?? ""
      };
    },

    onDragStart(card, sourceStatusId) {
      this.draggedCard = card;
      this.sourceColumn = sourceStatusId;
    },
    onDrop(targetStatusId) {
      if (this.sourceColumn === targetStatusId) return;
      if (this.sourceColumn !== null && this.draggedCard) {
        if (targetStatusId === this.lastStatusId) {
          this.showOutcomePopup = true;
        } else {
          this.changeCardColumn(targetStatusId);
          this.draggedCard.status = targetStatusId;

          this.updateAction(this.draggedCard);
          this.draggedCard = null;
          this.sourceColumn = null;
        }
      }
    },
    changeCardColumn(targetStatusId) {
      const draggedId = this.draggedCard.Id;
      const sourceStatusId = this.sourceColumn;

      this.actionsByStatus[sourceStatusId] = this.actionsByStatus[
        sourceStatusId
      ].filter((card) => card.Id !== draggedId);

      const jaExiste = this.actionsByStatus[targetStatusId].some(
        (card) => card.Id === draggedId
      );

      if (!jaExiste) {
        this.actionsByStatus[targetStatusId].push(this.draggedCard);
      }
    },
    chooseCategoryColor(actionType) {
      const hue = (actionType.Id * 137.508) % 360;
      const saturation = 60 + ((actionType.Id * 29) % 20);
      const lightness = 70 + ((actionType.Id * 23) % 10);
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    },

    openClosedActionDetails(card) {
      this.doneActionSelected = card;
      this.showDoneActionPopup = true;
    },

    openActionDetails(card) {
      this.actionDetailsSelected = card;
      this.showActionDetailsPopup = true;
    },

    closeActionDetails() {
      this.showActionDetailsPopup = false;
      this.actionDetailsSelected = null;
    },

    handleActionSave(payload) {
      if (!this.actionDetailsSelected) return;
      this.actionDetailsSelected.description = payload.description;
      this.actionDetailsSelected.assignedProfessional =
        payload.assignedProfessional || {};
      this.actionDetailsSelected.dueDate = payload.dueDate;
      this.updateAction(this.actionDetailsSelected);
    },

    openDeleteConfirm(card, index, statusId) {
      this.actionToDelete = card;
      this.actionToDeleteIndex = index;
      this.actionToDeleteColumn = statusId;
      this.showDeletePopup = true;
    },

    closeDeleteModal() {
      this.showDeletePopup = false;
      setTimeout(() => {
        this.actionToDelete = null;
        this.actionToDeleteIndex = null;
        this.actionToDeleteColumn = null;
      }, 300);
    },

    handleOutcomePopupClose() {
      this.draggedCard = null;
      this.sourceColumn = null;
      this.showOutcomePopup = false;
    },

    handleOutcomeSaved() {
      if (!this.draggedCard) return;

      this.draggedCard.status = this.lastStatusId;

      this.changeCardColumn(this.lastStatusId);

      this.updateAction(this.draggedCard);

      this.draggedCard = null;
      this.sourceColumn = null;
      this.showOutcomePopup = false;
    },
    handleActionCreated() {
      this.loadActions();
    },
    async handleDeleteAction() {
      await this.$HTTPUtilities.delete(
        this,
        `/api/Action/${this.actionToDelete.Id}/patient/${this.$route.params.id}`,
        () => {
          const statusId = this.actionToDeleteColumn;
          if (this.actionsByStatus[statusId]) {
            this.actionsByStatus[statusId].splice(this.actionToDeleteIndex, 1);
          }

          this.closeDeleteModal();

          this.$Utilities.controlAlert(null, this.alert, "delete");

          this.$nextTick(() => {
            setTimeout(() => {
              this.alert.show = false;
              this.alert.message = "";
              this.alert.type = "";
            }, 3000);
          });
        },
        this.alert
      );
    }
  }
};
</script>

<style scoped>
.kanban {
  display: flex;
  width: 100%;
  background-color: #f5f5f5;
  padding: 20px;
  box-sizing: border-box;
  overflow-x: auto;
  gap: 10px;
  margin-bottom: 20px;
  scrollbar-width: thin;
  scrollbar-color: #69287c #f5f5f5;
}

.kanban-column {
  flex: 1 1 0;
  min-width: 280px;
  max-width: 500px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 10px;
  height: 500px;
  overflow-y: auto;
  box-sizing: border-box;
}

.kanban-header {
  background-color: #69287c;
  color: white;
  padding: 10px;
  border-radius: 8px 8px 0 0;
  text-align: center;
  margin: -10px -10px 10px -10px;
  position: sticky;
  top: -10px;
  z-index: 1;
}

.kanban .card {
  background-color: white;
  margin: 10px 0;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  border: 2px solid;
  width: 100%;
  min-height: 120px;
  height: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
  transition: box-shadow 0.2s;
  box-sizing: border-box;
}

.kanban .card:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.kanban .kanban-card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 80px;
  width: 100%;
  gap: 12px;
}

.kanban .kanban-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.kanban .kanban-card-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
}

.kanban .card-info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.kanban .card-info-text {
  font-size: 13px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.kanban .card-professional {
  flex: 1;
  min-width: 0;
}

.kanban .card-due-date {
  flex-shrink: 0;
}

.kanban .card h3 {
  margin: 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  max-width: 100%;
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  flex: 1;
}

.kanban .delete-icon {
  width: 14px;
  height: 14px;
  opacity: 0.5;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.kanban .card:hover .delete-icon {
  opacity: 1;
}

.kanban::-webkit-scrollbar {
  height: 12px;
}

.kanban::-webkit-scrollbar-track {
  background: linear-gradient(to right, #e8e8e8, #f5f5f5);
  border-radius: 10px;
  margin: 0 10px;
}

.kanban::-webkit-scrollbar-thumb {
  background: linear-gradient(to right, #69287c, #8e44ad);
  border-radius: 10px;
  border: 2px solid #f5f5f5;
  transition: all 0.3s ease;
}

.kanban::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to right, #8e44ad, #69287c);
  border: 2px solid #e8e8e8;
}

.kanban::-webkit-scrollbar-thumb:active {
  background: linear-gradient(to right, #5a2069, #7d3c9b);
}

@media (max-width: 1214px) {
  .kanban {
    padding: 15px;
  }

  .kanban-column {
    min-width: 250px;
  }

  .kanban-header {
    font-size: 15px;
  }

  .kanban .card h3 {
    font-size: 13px;
  }

  .kanban .card-info-text {
    font-size: 12px;
  }
}

@media (max-width: 840px) {
  .kanban {
    flex-direction: column;
    padding: 10px;
    overflow-x: hidden;
  }

  .kanban-column {
    margin: 10px 0;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: auto;
    max-height: 400px;
  }

  .kanban .card {
    padding: 10px;
  }

  .kanban .card h3 {
    font-size: 12px;
  }

  .kanban .card-info-text {
    font-size: 11px;
  }

  .kanban-header {
    font-size: 14px;
  }
}
</style>
